<div class="ns-tab-page">
  <section class="ns-tab-intro">
    <div class="ns-tab-toolbar">
      <h2>Filters &amp; timing</h2>
    </div>
    <p>
      Who can be contacted, how often, and during which GMT hours. These rules apply to every invitation and reminder.
    </p>
  </section>

  <p class="ns-banner ns-banner--error" *ngIf="loadError">
    {{ loadError }}
    <button type="button" class="ns-link-btn" (click)="load()">Retry</button>
  </p>

  <div class="ns-tpl-loader" *ngIf="loading">
    <app-ltms-loader></app-ltms-loader>
  </div>

  <div class="ns-grid" *ngIf="!loading && !loadError">
    <section class="ns-card">
      <header class="ns-card__head">
        <div>
          <h3>Filters</h3>
          <p>Dedupe and daily limits for the same customer.</p>
        </div>
        <span class="ns-chip" [class.is-dirty]="filtersDirty">{{ filtersDirty ? 'Unsaved' : 'Saved' }}</span>
      </header>

      <div class="ns-list">
        <div class="ns-row ns-row--switch">
          <div class="ns-copy">
            <span class="ns-label">Single notification per day</span>
            <span class="ns-hint">At most one send per customer in a day for this project.</span>
          </div>
          <label class="ns-switch" [class.is-dirty]="isDirty('single_notification_per_day')">
            <input type="checkbox" [(ngModel)]="form.singleNotificationPerDay" name="singleNotificationPerDay" [disabled]="!isWriteAllowed">
            <span>{{ form.singleNotificationPerDay ? 'On' : 'Off' }}</span>
          </label>
        </div>

        <div class="ns-row">
          <div class="ns-copy">
            <span class="ns-label">Dedupe period</span>
            <span class="ns-hint">Hours after a response when no new invitation is sent. {{ limits.duplicatePreventDelay.min }}–{{ limits.duplicatePreventDelay.max }} hours (0–90 days).</span>
          </div>
          <div class="ns-control">
            <div class="ns-input-wrap">
              <input
                type="text"
                inputmode="numeric"
                class="ns-input"
                [(ngModel)]="form.duplicatePreventDelay"
                name="duplicatePreventDelay"
                maxlength="4"
                [disabled]="!isWriteAllowed"
                [class.is-invalid]="fieldErrors['duplicatePreventDelay']"
                [class.is-dirty]="!fieldErrors['duplicatePreventDelay'] && isDirty('duplicate_prevent_delay_hours')"
              >
              <span class="ns-suffix">hours</span>
            </div>
            <span class="ns-equiv" *ngIf="hoursAsDaysLabel(form.duplicatePreventDelay)">{{ hoursAsDaysLabel(form.duplicatePreventDelay) }}</span>
            <span class="ns-error" *ngIf="fieldErrors['duplicatePreventDelay']">{{ fieldErrors['duplicatePreventDelay'] }}</span>
          </div>
        </div>

        <div class="ns-row ns-row--pair">
          <div class="ns-copy">
            <span class="ns-label">Max in dedupe period</span>
            <span class="ns-hint">Requests and notifications allowed in that window. {{ limits.requestCount.min }}–{{ limits.requestCount.max }}.</span>
          </div>
          <div class="ns-control ns-control--pair">
            <label>
              <span class="ns-mini">Requests</span>
              <input
                type="text"
                inputmode="numeric"
                class="ns-input"
                [(ngModel)]="form.requestCount"
                name="requestCount"
                maxlength="2"
                [disabled]="!isWriteAllowed"
                [class.is-invalid]="fieldErrors['requestCount']"
                [class.is-dirty]="!fieldErrors['requestCount'] && isDirty('request_count')"
              >
              <span class="ns-error" *ngIf="fieldErrors['requestCount']">{{ fieldErrors['requestCount'] }}</span>
            </label>
            <label>
              <span class="ns-mini">Notifications</span>
              <input
                type="text"
                inputmode="numeric"
                class="ns-input"
                [(ngModel)]="form.notificationCount"
                name="notificationCount"
                maxlength="2"
                [disabled]="!isWriteAllowed"
                [class.is-invalid]="fieldErrors['notificationCount']"
                [class.is-dirty]="!fieldErrors['notificationCount'] && isDirty('notification_count')"
              >
              <span class="ns-error" *ngIf="fieldErrors['notificationCount']">{{ fieldErrors['notificationCount'] }}</span>
            </label>
          </div>
        </div>

        <div class="ns-row">
          <div class="ns-copy">
            <span class="ns-label">Dedupe field</span>
            <span class="ns-hint">Tag name. Leave empty for phone and email.</span>
          </div>
          <div class="ns-control ns-control--wide">
            <input
              type="text"
              class="ns-input"
              [(ngModel)]="form.dedupFieldIds"
              name="dedupFieldIds"
              placeholder="customer_id"
              [maxlength]="limits.dedupFieldIds.maxLength * limits.dedupFieldIds.maxItems"
              [disabled]="!isWriteAllowed"
              [class.is-invalid]="fieldErrors['dedupFieldIds']"
              [class.is-dirty]="!fieldErrors['dedupFieldIds'] && isDirty('dedup_field_ids')"
            >
            <span class="ns-error" *ngIf="fieldErrors['dedupFieldIds']">{{ fieldErrors['dedupFieldIds'] }}</span>
          </div>
        </div>
      </div>

      <footer class="ns-card__foot">
        <button type="button" class="ns-save ns-save--ghost" [disabled]="!filtersDirty || busy" (click)="resetFilters()">
          Discard
        </button>
        <button type="button" class="ns-save" [class.is-unauthorized]="!isWriteAllowed" [disabled]="isWriteAllowed && (!filtersDirty || busy)" (click)="saveFilters()">
          {{ savingFilters ? 'Saving…' : 'Save filters' }}
        </button>
      </footer>
    </section>

    <section class="ns-card">
      <header class="ns-card__head">
        <div>
          <h3>When messages can send</h3>
          <p>If this project has its own hours, Customer Notifier uses only those. Otherwise it uses the service hours.</p>
        </div>
        <div class="ns-card__tools">
          <span class="ns-chip" [class.is-dirty]="timingDirty">{{ timingDirty ? 'Unsaved' : 'Saved' }}</span>
          <div class="ns-clock">
            <span>Show hours in</span>
            <div class="ns-seg" role="group" aria-label="Hour timezone">
              <button type="button" [class.is-on]="hoursView === 'project'" (click)="setHoursView('project')">{{ zoneName }}</button>
              <button type="button" [class.is-on]="hoursView === 'gmt'" (click)="setHoursView('gmt')">GMT</button>
            </div>
          </div>
        </div>
      </header>

      <div class="ns-status ns-status--default" *ngIf="windowsMatchDefault">
        <span class="ns-status__badge">Default</span>
        <div class="ns-status__copy">
          <span class="ns-status__value" *ngIf="defaultHourLines.length === 1">
            {{ defaultHourLines[0].lead }}
            <small *ngIf="defaultHourLines[0].aside">{{ defaultHourLines[0].aside }}</small>
          </span>
          <ng-container *ngIf="defaultHourLines.length > 1">
            <span class="ns-status__lead">Messages can send in any of these hours</span>
            <ul class="ns-status__hours">
              <li *ngFor="let line of defaultHourLines">
                <span>{{ line.lead }}</span>
                <small>{{ line.aside }}</small>
              </li>
            </ul>
            <span class="ns-status__note">Outside every hour above, the notification service is OFF.</span>
          </ng-container>
        </div>
      </div>
      <div class="ns-status ns-status--custom" *ngIf="!windowsMatchDefault">
        <span class="ns-status__badge">Custom</span>
        <div class="ns-status__copy">
          <span class="ns-status__value" *ngIf="savedHourLines.length === 1">
            {{ savedHourLines[0].lead }}
            <small *ngIf="savedHourLines[0].aside">{{ savedHourLines[0].aside }}</small>
          </span>
          <ng-container *ngIf="savedHourLines.length > 1">
            <span class="ns-status__lead">Messages can send in any of these hours</span>
            <ul class="ns-status__hours">
              <li *ngFor="let line of savedHourLines">
                <span>{{ line.lead }}</span>
                <small>{{ line.aside }}</small>
              </li>
            </ul>
            <span class="ns-status__note">Outside every hour above, the notification service is OFF.</span>
          </ng-container>
        </div>
      </div>

      <p class="ns-error ns-error--block" *ngIf="fieldErrors['notifierTimings']">{{ fieldErrors['notifierTimings'] }}</p>

      <ul class="ns-windows" *ngIf="formHourLines.length" [class.is-dirty]="isDirty('notifier_timings')">
        <li *ngFor="let line of formHourLines; let i = index">
          <span class="ns-window__times">
            <span>{{ line.lead }}</span>
            <small>{{ line.aside }}</small>
          </span>
          <button
            type="button"
            class="ns-icon-btn"
            [class.is-unauthorized]="!isWriteAllowed"
            (click)="removeTiming(i)"
          >
            Remove
          </button>
        </li>
      </ul>

      <div class="ns-row ns-row--window">
        <div class="ns-copy">
          <span class="ns-label">{{ hoursView === 'gmt' ? 'Add hours in GMT' : 'Add hours in ' + zoneName }}</span>
          <span class="ns-hint">{{ hoursView === 'gmt' ? 'Same as the older dashboard. Project time is shown beside each hour.' : '12:00 AM is midnight. 2:00 AM is 02:00. GMT is saved for you.' }}</span>
        </div>
        <div class="ns-control ns-control--pair ns-control--window">
          <label>
            <span class="ns-mini">Start</span>
            <input type="time" class="ns-input" [(ngModel)]="draftStart" name="draftStart" [disabled]="!isWriteAllowed">
          </label>
          <label>
            <span class="ns-mini">End</span>
            <input type="time" class="ns-input" [(ngModel)]="draftEnd" name="draftEnd" [disabled]="!isWriteAllowed">
          </label>
          <button type="button" class="ns-save ns-save--sm" [class.is-unauthorized]="!isWriteAllowed" (click)="addTiming()">
            Add
          </button>
        </div>
      </div>
      <p class="ns-hint" *ngIf="draftClockNote() as note">{{ note }}</p>

      <footer class="ns-card__foot">
        <button type="button" class="ns-save ns-save--ghost" [disabled]="windowsMatchDefault || busy" [class.is-unauthorized]="!isWriteAllowed" (click)="useDefaultWindows()">
          Use default
        </button>
        <button type="button" class="ns-save ns-save--ghost" [disabled]="!timingDirty || busy" (click)="resetTiming()">
          Discard
        </button>
        <button type="button" class="ns-save" [class.is-unauthorized]="!isWriteAllowed" [disabled]="isWriteAllowed && (!timingDirty || busy)" (click)="saveTiming()">
          {{ savingTiming ? 'Saving…' : 'Save hours' }}
        </button>
      </footer>
    </section>

    <section class="ns-card">
      <header class="ns-card__head">
        <div>
          <h3>How far back to send</h3>
          <p>Customer Notifier only picks up requests created within this many days. Older requests are skipped.</p>
        </div>
        <span class="ns-chip" [class.is-dirty]="processingDirty">{{ processingDirty ? 'Unsaved' : 'Saved' }}</span>
      </header>

      <div class="ns-status ns-status--default" *ngIf="processingMatchesDefault">
        <span class="ns-status__badge">Default</span>
        <span class="ns-status__value">{{ defaults.date_limit_interval }} days</span>
      </div>
      <div class="ns-status ns-status--custom" *ngIf="!processingMatchesDefault">
        <span class="ns-status__badge">Custom</span>
        <span class="ns-status__value">{{ form.dateLimitInterval }} days</span>
      </div>

      <div class="ns-row">
        <div class="ns-copy">
          <span class="ns-label">Days</span>
          <span class="ns-hint">{{ limits.dateLimitInterval.min }}–{{ limits.dateLimitInterval.max }} days.</span>
        </div>
        <div class="ns-control">
          <div class="ns-input-wrap">
            <input
              type="text"
              inputmode="numeric"
              class="ns-input"
              [(ngModel)]="form.dateLimitInterval"
              name="dateLimitInterval"
              maxlength="2"
              [disabled]="!isWriteAllowed"
              [placeholder]="defaults.date_limit_interval"
              [class.is-invalid]="fieldErrors['dateLimitInterval']"
              [class.is-dirty]="!fieldErrors['dateLimitInterval'] && isDirty('date_limit_interval')"
            >
            <span class="ns-suffix">days</span>
          </div>
          <span class="ns-error" *ngIf="fieldErrors['dateLimitInterval']">{{ fieldErrors['dateLimitInterval'] }}</span>
        </div>
      </div>

      <footer class="ns-card__foot">
        <button type="button" class="ns-save ns-save--ghost" [disabled]="processingMatchesDefault || busy" [class.is-unauthorized]="!isWriteAllowed" (click)="useDefaultProcessing()">
          Use default
        </button>
        <button type="button" class="ns-save ns-save--ghost" [disabled]="!processingDirty || busy" (click)="resetProcessing()">
          Discard
        </button>
        <button type="button" class="ns-save" [class.is-unauthorized]="!isWriteAllowed" [disabled]="isWriteAllowed && (!processingDirty || busy)" (click)="saveProcessing()">
          {{ savingProcessing ? 'Saving…' : 'Save period' }}
        </button>
      </footer>
    </section>

    <section class="ns-card">
      <header class="ns-card__head">
        <div>
          <h3>Link expiration</h3>
          <p>
            How long a customer can open the feedback link after a request is created. The clock starts at request creation, not when the message is sent.
          </p>
        </div>
        <span class="ns-chip" [class.is-dirty]="expirationDirty">{{ expirationDirty ? 'Unsaved' : 'Saved' }}</span>
      </header>

      <div class="ns-list">
        <div class="ns-row">
          <div class="ns-copy">
            <span class="ns-label">Expires after</span>
            <span class="ns-hint">
              {{ limits.expirationDelay.min }}–{{ limits.expirationDelay.max }} hours (up to 90 days). After this, the link no longer loads the survey.
            </span>
          </div>
          <div class="ns-control">
            <div class="ns-input-wrap">
              <input
                type="text"
                inputmode="numeric"
                class="ns-input"
                [(ngModel)]="form.expirationDelay"
                name="expirationDelay"
                maxlength="4"
                [disabled]="!isWriteAllowed"
                [class.is-invalid]="fieldErrors['expirationDelay']"
                [class.is-dirty]="!fieldErrors['expirationDelay'] && isDirty('expiration_delay_hours')"
              >
              <span class="ns-suffix">hours</span>
            </div>
            <span class="ns-equiv" *ngIf="hoursAsDaysLabel(form.expirationDelay)">{{ hoursAsDaysLabel(form.expirationDelay) }}</span>
            <span class="ns-error" *ngIf="fieldErrors['expirationDelay']">{{ fieldErrors['expirationDelay'] }}</span>
          </div>
        </div>
      </div>

      <footer class="ns-card__foot">
        <button type="button" class="ns-save ns-save--ghost" [disabled]="!expirationDirty || busy" (click)="resetExpiration()">
          Discard
        </button>
        <button type="button" class="ns-save" [class.is-unauthorized]="!isWriteAllowed" [disabled]="isWriteAllowed && (!expirationDirty || busy)" (click)="saveExpiration()">
          {{ savingExpiration ? 'Saving…' : 'Save link expiration' }}
        </button>
      </footer>
    </section>

    <section class="ns-card">
      <header class="ns-card__head">
        <div>
          <h3>On-demand requests</h3>
          <p>
            Extra sends triggered from Outbox. Available only after Invites &amp; reminders is Live and at least one invitation attempt has a channel.
          </p>
        </div>
        <span class="ns-chip" [class.is-dirty]="onDemandDirty">{{ onDemandDirty ? 'Unsaved' : 'Saved' }}</span>
      </header>

      <p class="ns-banner ns-banner--warn" *ngIf="!onDemandReady">
        Finish Invites &amp; reminders first: set the service to Live, then save at least one attempt with SMS, email, or WhatsApp.
      </p>

      <div class="ns-list">
        <div class="ns-row ns-row--switch">
          <div class="ns-copy">
            <span class="ns-label">Enable on-demand trigger</span>
            <span class="ns-hint">
              Turns on Backup Attempt in Notification Setup and on-demand actions in Outbox for this project.
            </span>
          </div>
          <label class="ns-switch" [class.is-dirty]="isDirty('is_on_demand_trigger_enabled')">
            <input
              type="checkbox"
              [(ngModel)]="form.isOnDemandTriggerEnabled"
              name="isOnDemandTriggerEnabled"
              [disabled]="onDemandSwitchDisabled"
            >
            <span>{{ form.isOnDemandTriggerEnabled ? 'On' : 'Off' }}</span>
          </label>
        </div>
      </div>

      <footer class="ns-card__foot">
        <button type="button" class="ns-save ns-save--ghost" [disabled]="!onDemandDirty || busy" (click)="resetOnDemand()">
          Discard
        </button>
        <button type="button" class="ns-save" [class.is-unauthorized]="!isWriteAllowed" [disabled]="isWriteAllowed && (!onDemandDirty || busy)" (click)="saveOnDemand()">
          {{ savingOnDemand ? 'Saving…' : 'Save on-demand' }}
        </button>
      </footer>
    </section>
  </div>

  <div class="ns-confirm" *ngIf="confirmSave">
    <div class="ns-confirm__overlay" (click)="cancelSaveConfirm()"></div>
    <div class="ns-confirm__card">
      <h3>{{ confirmSave.title }}</h3>
      <p>{{ confirmSave.message }}</p>
      <div class="ns-confirm__actions">
        <button type="button" class="ns-save ns-save--ghost" (click)="cancelSaveConfirm()">Don't continue</button>
        <button type="button" class="ns-save" [disabled]="busy" (click)="confirmSaveNow()">Yes, continue</button>
      </div>
    </div>
  </div>
</div>
